import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Calendar, CheckCircle2, ArrowRight, Star, Quote, BadgeDollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { SectionTitle, CurveDivider } from '../components/Sections';
import ContactForm from '../components/ContactForm';
import AppointmentModal from '../components/AppointmentModal';
import { homeContent as fbHome, faqs as fbFaqs, siteSettings as fbSettings, IMAGES } from '../data/mock';
import { useContent } from '../context/ContentContext';
import { getIcon } from '../lib/icons';
import Seo from '../components/Seo';
import { businessLd, faqLd } from '../lib/seo';

const Home = () => {
  const { homeContent = fbHome, faqs = fbFaqs, siteSettings = fbSettings, serviceAreas = [], rebateBanner } = useContent();
  const [slide, setSlide] = useState(0);
  const [apptOpen, setApptOpen] = useState(false);
  const slides = homeContent.heroSlides;

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);

  return (
    <div>
      <Seo
        title="HVAC Cortez CO | Heating & Air Conditioning Repair | Comfort Air Mechanical"
        description="Comfort Air Mechanical is Cortez, Colorado's trusted HVAC contractor since 1988. Heating, air conditioning, refrigeration & sheet metal service across the Four Corners. Call (970) 565-7893 for a free estimate."
        path="/"
        image={IMAGES.heroFamily}
        jsonLd={[businessLd(siteSettings, { image: IMAGES.heroFamily, areaServed: serviceAreas }), faqLd(faqs)]}
      />
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center overflow-hidden bg-brand-bluedeep">
        {slides.map((s, i) => (
          <div key={i} className={`absolute inset-0 transition-opacity duration-1000 ${i === slide ? 'opacity-100' : 'opacity-0'}`}>
            <img src={s.image} alt={s.title} className={`w-full h-full object-cover ${i === slide ? 'animate-kenburns' : ''}`} />
            <div className="absolute inset-0 bg-gradient-to-r from-brand-bluedeep/95 via-brand-bluedeep/80 to-brand-bluedark/40" />
          </div>
        ))}

        <div className="container-c relative z-10 py-24">
          <div className="max-w-2xl animate-fade-up">
            <div className="inline-flex items-center gap-2 bg-brand-green/20 border border-brand-green/40 rounded-full px-4 py-1.5 text-brand-green text-sm font-semibold mb-6">
              <Star size={15} fill="currentColor" /> {siteSettings.tagline}
            </div>
            <div className="text-brand-green font-display font-semibold text-xl md:text-2xl mb-2">{slides[slide].kicker}</div>
            <h1 className="font-display font-extrabold text-5xl md:text-6xl text-white leading-[1.05]">
              {slides[slide].title}
            </h1>
            <ul className="mt-8 space-y-3">
              {homeContent.heroBullets.map((b) => (
                <li key={b} className="flex items-center gap-3 text-white/90 text-lg">
                  <CheckCircle2 className="text-brand-green shrink-0" size={22} /> {b}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3 mt-9">
              <Button onClick={() => setApptOpen(true)} className="bg-brand-green hover:bg-brand-greendark text-white font-semibold h-13 px-7 text-base gap-2 shadow-xl h-12">
                <Calendar size={18} /> Request Appointment
              </Button>
              <a href={`tel:${siteSettings.phoneRaw}`}>
                <Button variant="outline" className="border-white/40 bg-white/5 text-white hover:bg-white hover:text-brand-bluedark font-semibold h-12 px-7 text-base gap-2">
                  <Phone size={18} /> {siteSettings.phone}
                </Button>
              </a>
            </div>
          </div>
        </div>

        {/* slide dots */}
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-10 flex gap-2">
          {slides.map((_, i) => (
            <button key={i} onClick={() => setSlide(i)} className={`h-2 rounded-full transition-all ${i === slide ? 'w-8 bg-brand-green' : 'w-2 bg-white/40'}`} aria-label={`Slide ${i + 1}`} />
          ))}
        </div>
        <CurveDivider />
      </section>

      {/* WHY CHOOSE */}
      <section className="section-pad bg-white">
        <div className="container-c">
          <SectionTitle kicker="Welcome to Comfort Air" title={homeContent.whyChoose.heading} />
          <p className="text-slate-600 text-lg leading-relaxed mt-6 max-w-4xl">{homeContent.whyChoose.paragraph}</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {homeContent.whyChoose.features.map((f) => {
              const Icon = getIcon(f.icon);
              return (
                <div key={f.title} className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover-lift">
                  <div className="w-14 h-14 rounded-xl bg-brand-bluelight flex items-center justify-center mb-5">
                    <Icon className="text-brand-blue" size={28} />
                  </div>
                  <h3 className="font-display font-bold text-xl text-brand-bluedark mb-2">{f.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{f.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative py-16 bg-brand-bluedark overflow-hidden">
        <img src={IMAGES.mountains} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="container-c relative grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {homeContent.stats.map((s) => (
            <div key={s.label}>
              <div className="font-display font-extrabold text-4xl md:text-5xl text-brand-green">{s.value}</div>
              <div className="text-white/80 mt-2 font-medium">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* REBATE BANNER */}
      {rebateBanner?.enabled && (
        <section className="py-10 bg-brand-greenlight">
          <div className="container-c">
            <div className="flex flex-col md:flex-row items-center gap-5 bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-brand-green/25">
              <div className="w-14 h-14 rounded-xl bg-brand-green/15 flex items-center justify-center shrink-0">
                <BadgeDollarSign className="text-brand-green" size={30} />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="font-display font-bold text-xl text-brand-bluedark">{rebateBanner.heading}</h3>
                <p className="text-slate-600 mt-1 leading-relaxed">{rebateBanner.text}</p>
              </div>
              <Link to="/contact">
                <Button className="bg-brand-green hover:bg-brand-greendark text-white font-semibold gap-2 whitespace-nowrap h-11 px-6">
                  Ask Us How <ArrowRight size={17} />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* SERVICES */}
      <section className="section-pad bg-brand-bluelight">
        <div className="container-c">
          <SectionTitle kicker="What We Do" title="Our Services" center />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {homeContent.services.map((s) => {
              const Icon = getIcon(s.icon);
              return (
                <Link key={s.title} to={s.path} className="group bg-white rounded-2xl p-7 shadow-sm hover-lift flex flex-col">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-brand-blue to-brand-bluedark flex items-center justify-center mb-5 group-hover:from-brand-green group-hover:to-brand-greendark transition-all">
                    <Icon className="text-white" size={30} />
                  </div>
                  <h3 className="font-display font-bold text-lg text-brand-bluedark mb-2">{s.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed flex-1">{s.text}</p>
                  <span className="mt-4 text-brand-green font-semibold text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    Learn More <ArrowRight size={16} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-pad bg-white">
        <div className="container-c">
          <SectionTitle kicker="Testimonials" title="What Our Customers Say" center />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {homeContent.testimonials.map((t) => (
              <div key={t.name} className="bg-brand-bluelight rounded-2xl p-8 relative hover-lift">
                <Quote className="text-brand-green/40 absolute top-6 right-6" size={40} />
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} size={18} className="text-brand-green" fill="currentColor" />)}
                </div>
                <p className="text-slate-700 leading-relaxed italic">"{t.text}"</p>
                <div className="mt-5 pt-5 border-t border-brand-blue/10">
                  <div className="font-display font-bold text-brand-bluedark">{t.name}</div>
                  <div className="text-sm text-slate-500">{t.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-pad bg-brand-bluelight">
        <div className="container-c grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <SectionTitle kicker="Heating & Cooling FAQ" title="Frequently Asked Questions" />
            <p className="text-slate-600 mt-6 leading-relaxed">Have a question about your HVAC system? Here are answers to some of the questions we hear most. Still curious? Give us a call and we'll be happy to help.</p>
            <div className="mt-8 bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div className="font-display font-bold text-brand-bluedark text-lg mb-1">Certified Elite Plus Contractor</div>
              <p className="text-slate-600 text-sm">Backed by a 12-year manufacturer warranty on qualifying systems.</p>
            </div>
          </div>
          <Accordion type="single" collapsible className="w-full space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-xl border border-slate-100 px-5 shadow-sm">
                <AccordionTrigger className="font-display font-semibold text-brand-bluedark text-left hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA + FORM */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-brand-bluedeep">
        <img src={IMAGES.mountains2} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-br from-brand-bluedeep/90 to-brand-bluedark/80" />
        <div className="container-c relative grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="text-white">
            <div className="text-brand-green font-bold uppercase tracking-widest text-sm mb-3">Schedule Today</div>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl leading-tight">We're here for all of your HVAC needs</h2>
            <p className="text-white/80 text-lg mt-5 leading-relaxed">From professional installations to expert repair services, our fully licensed and insured technicians are ready to help. Call now for a FREE estimate.</p>
            <a href={`tel:${siteSettings.phoneRaw}`} className="inline-flex items-center gap-3 mt-8 bg-white/10 border border-white/20 rounded-xl px-6 py-4 hover:bg-white/20 transition-colors">
              <Phone className="text-brand-green" size={28} />
              <div>
                <div className="text-white/60 text-sm">Call us now</div>
                <div className="font-display font-bold text-2xl">{siteSettings.phone}</div>
              </div>
            </a>
          </div>
          <ContactForm variant="onImage" storeKey="appointments" />
        </div>
      </section>

      <AppointmentModal open={apptOpen} onClose={() => setApptOpen(false)} />
    </div>
  );
};

export default Home;
